import { BookmarkFilledIcon, BookmarkIcon } from '@radix-ui/react-icons'
import { Slot } from '@radix-ui/react-slot'
import { clsx } from 'clsx'
import {
  PublicRepo,
  PublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'
import * as React from 'react'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { Button } from '~/sotano-kit/primitives/button'
import { useBookmark } from '~/hooks-data/use-bookmark'

type BookmarkProps =
  | {
      team: Pick<PublicTeam, 'id' | 'slug'>
      repository: Pick<PublicRepo, 'id' | 'slug'>
      stats?: number
      intent?: 'asChild'
      children?: ({
        isBookmarked,
      }: {
        isBookmarked: boolean
      }) => React.ReactNode
    }
  | {
      children?: React.ReactNode
      team: Pick<PublicTeam, 'id' | 'slug'>
      repository: Pick<PublicRepo, 'id' | 'slug'>
      stats?: number
      intent?: 'counter' | 'large'
    }

export const Bookmark = ({
  className,
  team,
  repository,
  stats,
  ...rest
}: Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> &
  BookmarkProps) => {
  const { isBookmarked, handleToggleBookmark } = useBookmark({
    team: {
      id: team.id,
      slug: team.slug,
    },
    repo: {
      id: repository.id,
      slug: repository.slug,
    },
  })

  if (rest.intent === 'asChild') {
    return (
      <Slot
        {...rest}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          handleToggleBookmark()
        }}
      >
        {typeof rest.children === 'function'
          ? rest.children({ isBookmarked })
          : rest.children}
      </Slot>
    )
  }

  if (rest.intent === 'large') {
    return (
      <Button
        size="small"
        intent="secondary"
        className={clsx('inline-flex items-center', className)}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          handleToggleBookmark()
        }}
      >
        {isBookmarked ? (
          <BookmarkFilledIcon height={14} width={14} />
        ) : (
          <BookmarkIcon height={14} width={14} />
        )}
        Bookmark
      </Button>
    )
  }

  const { intent: _, ...props } = rest

  return (
    <IconButton
      {...props}
      aria-label="Bookmark this repository"
      title="Bookmark this repository"
      className={clsx(
        className,
        'flex items-center hover:border-dark-border-solid font-normal cursor-default border border-transparent'
      )}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        handleToggleBookmark()
      }}
    >
      {isBookmarked ? (
        <BookmarkFilledIcon height={16} width={16} />
      ) : (
        <BookmarkIcon height={16} width={16} />
      )}
      {stats !== undefined && (
        <span className="text-xs ml-0.5 pr-0.5">{stats}</span>
      )}
    </IconButton>
  )
}
